export const CATEGORY_MEMBER_ROLES = {
  DRIVER: "driver",
  NAVIGATOR: "navigator",
  MECHANIC: "mechanic",
};

export const ALLOWED_MEMBER_ROLES = [
  CATEGORY_MEMBER_ROLES.DRIVER,
  CATEGORY_MEMBER_ROLES.NAVIGATOR,
  CATEGORY_MEMBER_ROLES.MECHANIC,
];

export const SELECTABLE_MEMBER_ROLE_OPTIONS = [
  { value: CATEGORY_MEMBER_ROLES.NAVIGATOR, label: "Codriver" },
  { value: CATEGORY_MEMBER_ROLES.MECHANIC, label: "Mechanic" },
];

/** Ensure seat 2 is Codriver when Codriver is allowed. */
export const applyCodriverSeatRules = (
  maxMembers,
  previousRoles = [],
  navigatorAllowed = false
) => {
  const count = Math.max(0, Math.floor(Number(maxMembers)) || 0);
  const roles = buildMemberRoles(count, previousRoles);

  if (navigatorAllowed && count >= 2) {
    roles[1] = CATEGORY_MEMBER_ROLES.NAVIGATOR;
    if (
      count === 3 &&
      roles[2] === CATEGORY_MEMBER_ROLES.NAVIGATOR
    ) {
      roles[2] = CATEGORY_MEMBER_ROLES.MECHANIC;
    }
  }

  return roles;
};

const PRESET_SELECTABLE_ROLES = [
  CATEGORY_MEMBER_ROLES.NAVIGATOR,
  CATEGORY_MEMBER_ROLES.MECHANIC,
];

const parseNonNegativeInteger = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
};

/**
 * Resize / normalize member_roles for a given max_members count.
 * Index 0 is always driver when at least one seat exists.
 */
export const buildMemberRoles = (maxMembers, previousRoles = []) => {
  const count = Math.max(0, Math.floor(Number(maxMembers)) || 0);
  if (count === 0) return [];

  const prev = Array.isArray(previousRoles) ? previousRoles : [];
  const roles = Array.from({ length: count }, (_, index) => {
    if (index === 0) return CATEGORY_MEMBER_ROLES.DRIVER;
    return typeof prev[index] === "string" ? prev[index] : "";
  });

  if (count === 3) {
    const second = roles[1];
    const third = roles[2];
    if (
      PRESET_SELECTABLE_ROLES.includes(second) &&
      !PRESET_SELECTABLE_ROLES.includes(third)
    ) {
      roles[2] = PRESET_SELECTABLE_ROLES.find((role) => role !== second) || "";
    } else if (
      PRESET_SELECTABLE_ROLES.includes(third) &&
      !PRESET_SELECTABLE_ROLES.includes(second)
    ) {
      roles[1] = PRESET_SELECTABLE_ROLES.find((role) => role !== third) || "";
    } else if (PRESET_SELECTABLE_ROLES.includes(second) && second === third) {
      roles[2] = PRESET_SELECTABLE_ROLES.find((role) => role !== second) || "";
    }
  }

  return roles;
};

export const setMemberRoleAt = (roles, index, value, maxMembers) => {
  if (index === 0) return buildMemberRoles(maxMembers, roles);

  const count = Math.max(0, Math.floor(Number(maxMembers)) || 0);
  const next = buildMemberRoles(count, roles);
  next[index] = value;

  if (
    count === 3 &&
    (index === 1 || index === 2) &&
    PRESET_SELECTABLE_ROLES.includes(value)
  ) {
    const otherIndex = index === 1 ? 2 : 1;
    next[otherIndex] =
      PRESET_SELECTABLE_ROLES.find((role) => role !== value) || "";
  }

  return next;
};

export const membersFromRoles = (roles = []) => {
  return (Array.isArray(roles) ? roles : [])
    .filter((role) => ALLOWED_MEMBER_ROLES.includes(role))
    .map((role) => ({ role }));
};

export const rolesFromMembers = (members, maxMembers) => {
  const fromApi = Array.isArray(members)
    ? members
        .map((item) => (typeof item === "string" ? item : item?.role))
        .filter(Boolean)
    : [];
  return buildMemberRoles(maxMembers, fromApi);
};

export const getTypeIdValue = (typeId) => {
  if (!typeId) return "";
  if (typeof typeId === "object") return typeId._id || typeId.id || "";
  return String(typeId);
};

export const getCategoryType = (category) => {
  const type = category?.typeId || category?.type;
  if (!type) return null;
  if (typeof type === "string") return { _id: type };
  return type;
};

export const getCategoryTypeName = (category) => {
  const type = getCategoryType(category);
  if (!type) return "";
  return type.name || type.title || "";
};

export const initialCategoryForm = {
  title: "",
  description: "",
  typeId: "",
  minCC: "",
  maxCC: "",
  max_members: "1",
  member_roles: [CATEGORY_MEMBER_ROLES.DRIVER],
  navigator_allowed: false,
  consent: "",
  status: "active",
  image: "",
  imageFile: null,
};

export const normalizeCategories = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.categories || data?.items || [];
};

export const normalizeCategory = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data._id || data.id || data.title || data.key) return data;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data.category || null;
};

export const isJeepRallyAdmin = () => true;

export const toCategoryKey = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const getCategoryApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }
  return message;
};

export const filterCategories = (categories, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return categories;

  return categories.filter((category) => {
    const title = category?.title?.toLowerCase() || "";
    const key = category?.key?.toLowerCase() || "";
    const description = category?.description?.toLowerCase() || "";
    const typeName = getCategoryTypeName(category).toLowerCase();
    const routeTitle = getCategoryRouteTitle(category).toLowerCase();
    return (
      title.includes(term) ||
      key.includes(term) ||
      description.includes(term) ||
      typeName.includes(term) ||
      routeTitle.includes(term)
    );
  });
};

export const getCategoryRoute = (category) => {
  const route = category?.route_id || category?.route;
  if (!route) return null;
  if (typeof route === "string") return { _id: route };
  return route;
};

export const getCategoryRouteTitle = (category) => {
  const route = getCategoryRoute(category);
  if (!route) return "";
  return route.title || route.name || "";
};

export const getCategoryRouteSummary = (category) => {
  const route = getCategoryRoute(category);
  if (!route || (!route.title && !route.name && route.total_distance_km == null)) {
    return "No route assigned";
  }

  const parts = [route.title || route.name || "Route"];
  if (route.total_distance_km != null && route.total_distance_km !== "") {
    parts.push(`${route.total_distance_km} km`);
  }
  if (route.estimated_time) parts.push(route.estimated_time);
  return parts.join(" · ");
};

export const mapCategoryToForm = (category) => {
  const navigatorAllowed = category?.navigator_allowed === true;
  let maxMembersCount =
    category?.max_members === 0 || category?.max_members
      ? Math.max(0, Number(category.max_members) || 0)
      : 1;

  if (navigatorAllowed && maxMembersCount < 2) {
    maxMembersCount = 2;
  }

  const maxMembers = String(maxMembersCount);

  return {
    title: category?.title || "",
    description: category?.description || "",
    typeId: getTypeIdValue(category?.typeId),
    minCC:
      category?.minCC === 0 || category?.minCC
        ? String(category.minCC)
        : "",
    maxCC:
      category?.maxCC === 0 || category?.maxCC
        ? String(category.maxCC)
        : "",
    max_members: maxMembers,
    member_roles: applyCodriverSeatRules(
      maxMembersCount,
      rolesFromMembers(category?.members, maxMembersCount),
      navigatorAllowed
    ),
    navigator_allowed: navigatorAllowed,
    consent: category?.consent || "",
    status: category?.status === "inactive" ? "inactive" : "active",
    image: category?.image || "",
    imageFile: null,
  };
};

export const validateCategoryForm = (form, options = {}) => {
  const { existingCategories = [], currentId = null } = options;
  const errors = {};
  const title = form.title?.trim() || "";

  if (!title) {
    errors.title = "Category title is required.";
  } else {
    const duplicate = existingCategories.find((category) => {
      const sameTitle =
        String(category?.title || "")
          .trim()
          .toLowerCase() === title.toLowerCase();
      const categoryId = category?._id || category?.id;
      return sameTitle && categoryId !== currentId;
    });
    if (duplicate) {
      errors.title = "Category title already exists.";
    }
  }

  if (!String(form.typeId || "").trim()) {
    errors.typeId = "Type is required.";
  }

  const minCC = parseNonNegativeInteger(form.minCC);
  if (form.minCC === "" || form.minCC === null || form.minCC === undefined) {
    errors.minCC = "minCC is required.";
  } else if (minCC === null) {
    errors.minCC = "minCC must be a number greater than or equal to 0.";
  }

  if (form.maxCC !== "" && form.maxCC !== null && form.maxCC !== undefined) {
    const maxCC = parseNonNegativeInteger(form.maxCC);
    if (maxCC === null) {
      errors.maxCC = "maxCC must be a number greater than or equal to 0.";
    } else if (minCC !== null && maxCC < minCC) {
      errors.maxCC = "maxCC must be greater than or equal to minCC.";
    }
  }

  const maxMembers =
    form.max_members === "" ||
    form.max_members === null ||
    form.max_members === undefined
      ? null
      : Number(form.max_members);

  if (form.max_members === "" || form.max_members === null || form.max_members === undefined) {
    errors.max_members = "max_members is required.";
  } else if (!Number.isInteger(maxMembers)) {
    errors.max_members = "max_members is required.";
  } else if (maxMembers < 0) {
    errors.max_members = "max_members must be at least 0.";
  } else if (form.navigator_allowed && maxMembers < 2) {
    errors.max_members =
      "Max members must be at least 2 when Codriver is allowed.";
  }

  if (typeof form.navigator_allowed !== "boolean") {
    errors.navigator_allowed = "navigator_allowed is required.";
  }

  if (form.navigator_allowed && Number.isInteger(maxMembers) && maxMembers >= 2) {
    const roles = buildMemberRoles(maxMembers, form.member_roles || []);
    if (roles[1] !== CATEGORY_MEMBER_ROLES.NAVIGATOR) {
      errors.member_roles =
        "Seat 2 must be Codriver when Codriver is allowed.";
    }
  }

  if (form.status && form.status !== "active" && form.status !== "inactive") {
    errors.status = "Status must be active or inactive.";
  }

  return errors;
};

export const isConsentHtmlEmpty = (html) => {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return !text;
};

export const normalizeConsentHtml = (html) => {
  if (isConsentHtmlEmpty(html)) return "";
  return String(html).trim();
};

const membersKey = (roles) => JSON.stringify(membersFromRoles(roles));

export const buildCategoryFormData = (form, options = {}) => {
  const { isEdit = false, initialForm = null } = options;
  const formData = new FormData();

  const title = form.title.trim();
  const description = form.description ?? "";
  const typeId = form.typeId?.trim() || "";
  const minCC = parseNonNegativeInteger(form.minCC);
  const maxCCRaw = form.maxCC;
  const maxCC =
    maxCCRaw === "" || maxCCRaw === null || maxCCRaw === undefined
      ? null
      : parseNonNegativeInteger(maxCCRaw);
  const maxMembers = Number(form.max_members);
  const navigatorAllowed = Boolean(form.navigator_allowed);
  const consent = normalizeConsentHtml(form.consent);
  const status = form.status === "inactive" ? "inactive" : "active";
  const imageFile = form.imageFile instanceof File ? form.imageFile : null;
  const members = membersFromRoles(
    applyCodriverSeatRules(maxMembers, form.member_roles, navigatorAllowed)
  );

  if (!isEdit) {
    formData.append("title", title);
    formData.append("minCC", String(minCC ?? 0));
    if (maxCC !== null) formData.append("maxCC", String(maxCC));
    formData.append("typeId", typeId);
    if (description) formData.append("description", description);
    formData.append("max_members", String(maxMembers));
    formData.append("navigator_allowed", navigatorAllowed ? "true" : "false");
    if (consent) formData.append("consent", consent);
    formData.append("status", status);
    if (members.length) formData.append("members", JSON.stringify(members));
    if (imageFile) formData.append("image", imageFile);
    return formData;
  }

  const initialTitle = initialForm?.title?.trim() || "";
  const initialDescription = initialForm?.description ?? "";
  const initialTypeId = initialForm?.typeId?.trim() || "";
  const initialMinCC = parseNonNegativeInteger(initialForm?.minCC);
  const initialMaxCCRaw = initialForm?.maxCC;
  const initialMaxCC =
    initialMaxCCRaw === "" ||
    initialMaxCCRaw === null ||
    initialMaxCCRaw === undefined
      ? null
      : parseNonNegativeInteger(initialMaxCCRaw);
  const initialMaxMembers = Number(initialForm?.max_members);
  const initialNavigatorAllowed = Boolean(initialForm?.navigator_allowed);
  const initialConsent = normalizeConsentHtml(initialForm?.consent);
  const initialStatus =
    initialForm?.status === "inactive" ? "inactive" : "active";
  const initialMembersKey = membersKey(initialForm?.member_roles);

  if (title !== initialTitle) formData.append("title", title);
  if (description !== initialDescription) {
    formData.append("description", description);
  }
  if (typeId !== initialTypeId) {
    formData.append("typeId", typeId || "null");
  }
  if (minCC !== initialMinCC) formData.append("minCC", String(minCC ?? 0));
  if (maxCC !== initialMaxCC) {
    formData.append("maxCC", maxCC === null ? "" : String(maxCC));
  }
  if (maxMembers !== initialMaxMembers) {
    formData.append("max_members", String(maxMembers));
  }
  if (navigatorAllowed !== initialNavigatorAllowed) {
    formData.append("navigator_allowed", navigatorAllowed ? "true" : "false");
  }
  if (consent !== initialConsent) formData.append("consent", consent);
  if (status !== initialStatus) formData.append("status", status);
  if (membersKey(form.member_roles) !== initialMembersKey) {
    formData.append("members", JSON.stringify(members));
  }
  if (imageFile) formData.append("image", imageFile);

  return [...formData.keys()].length ? formData : null;
};
